import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  FileText, 
  CheckCircle2, 
  Clock, 
  ExternalLink, 
  Download,
  IdCard,
  Car,
  Shield,
  GraduationCap,
} from "lucide-react";

const getDocumentIcon = (type) => {
  switch(type) {
    case 'passport': return <IdCard className="w-4 h-4" />;
    case 'driver_license': return <Car className="w-4 h-4" />;
    case 'hms_card': return <Shield className="w-4 h-4" />;
    case 'certificate': return <GraduationCap className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
};

const getDocumentLabel = (type) => {
  switch(type) {
    case 'passport': return 'Passport / ID';
    case 'driver_license': return 'Driver License';
    case 'hms_card': return 'HMS Card';
    case 'certificate': return 'Certificate';
    case 'id_card': return 'ID Card';
    default: return 'Other Document';
  }
};

export const DocumentPreviewDialog = ({ 
  open, 
  onOpenChange, 
  document 
}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#23F7DD]" />
            Document Preview
          </DialogTitle>
        </DialogHeader>
        {document && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                {getDocumentIcon(document.type)}
                <div>
                  <p className="text-white font-medium">{getDocumentLabel(document.type)}</p>
                  <p className="text-xs text-[#94A3B8]">{document.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {document.verified ? (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Verified
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Pending
                  </span>
                )}
              </div>
            </div>
            
            {/* Document Preview Area */}
            <div className="border border-white/10 rounded-lg overflow-hidden bg-white/5">
              {document.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img 
                  src={`${baseUrl}${document.url}`}
                  alt={document.name}
                  className="w-full max-h-[500px] object-contain"
                />
              ) : document.url?.match(/\.pdf$/i) ? (
                <iframe
                  src={`${baseUrl}${document.url}`}
                  className="w-full h-[500px]"
                  title={document.name}
                />
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-16 h-16 text-[#475569] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">Preview not available for this file type</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-2">
              <a
                href={`${baseUrl}${document.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#23F7DD] hover:text-[#70FFEF] transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open in new tab
              </a>
              <a
                href={`${baseUrl}${document.url}`}
                download
                className="flex items-center gap-2 px-4 py-2 bg-[#23F7DD] text-[#050505] rounded-lg hover:bg-[#70FFEF] transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { getDocumentIcon, getDocumentLabel };
